<template>
  <q-page class="bg-light q-pa-lg">
    <div class="container-md">
      <!-- Заглавие -->
      <div class="row items-center q-mb-md no-pdf">
        <div class="col-2"></div>

        <div class="col-8 text-center">
          <div class="text-h6 text-mint text-bold">
            Преглед на поднесени барања од {{ companyName }}
          </div>
        </div>

        <div class="col-2 flex justify-end">
          <q-btn
            flat
            dense
            round
            icon="close"
            class="btn-clean"
            @click="goBack"
          >
            <q-tooltip>Затвори</q-tooltip>
          </q-btn>
        </div>
      </div>

      <!-- Филтри -->
      <q-card class="q-pa-md shadow-xs rounded-2xl q-mb-md bg-white no-pdf filter-card">
        <div class="row q-col-gutter-md items-center">
          <div class="col-12 col-md-3">
            <q-input
              v-model="filter.brojBaranje"
              outlined
              dense
              color="mint"
              clearable
              label="Број на барање"
              maxlength="10"
              @update:model-value="onBrojBaranjeInput"
            />
          </div>

          <div class="col-12 col-md-3">
            <q-input
              v-model="filter.from"
              type="date"
              outlined
              dense
              color="mint"
              :disable="hasBrojBaranje"
            >
              <template v-slot:label>
                <span>
                  Од датум
                  <span v-if="!hasBrojBaranje" class="required-star">*</span>
                </span>
              </template>
            </q-input>
          </div>

          <div class="col-12 col-md-3">
            <q-input
              v-model="filter.to"
              type="date"
              outlined
              dense
              color="mint"
              :disable="hasBrojBaranje"
            >
              <template v-slot:label>
                <span>
                  До датум
                  <span v-if="!hasBrojBaranje" class="required-star">*</span>
                </span>
              </template>
            </q-input>
          </div>

          <div class="col-12 col-md-3">
            <q-select
              v-model="filter.status"
              :options="statusOptions"
              option-value="SifraID"
              option-label="Opis"
              label="Статус"
              outlined
              dense
              color="mint"
              clearable
              :disable="hasBrojBaranje"
            >
              <template v-slot:prepend>
                <q-icon name="flag" color="mint" />
              </template>
            </q-select>
          </div>

          <div class="col-12">
            <div class="filter-buttons-center">
              <q-btn
                color="mint"
                label="Пребарај"
                class="text-white action-btn"
                icon="search"
                @click="applyFilter"
              />

              <q-btn
                outline
                color="mint"
                label="Исчисти"
                class="action-btn"
                icon="filter_alt_off"
                @click="resetFilter"
              />

              <q-btn
                outline
                color="mint"
                label="Печати"
                class="action-btn"
                icon="print"
                :loading="loadingPdf"
                @click="exportPdf"
              />
            </div>
          </div>
        </div>
      </q-card>

      <!-- Табела -->
      <q-card class="table-card q-pa-md shadow-xs rounded-2xl">
        <q-table
          :data="baranja"
          :columns="columns"
          row-key="ID"
          flat
          bordered
          dense
          separator="horizontal"
          no-data-label="Нема податоци за овој период."
          class="table-clean clickable-table"
          :pagination.sync="pagination"
          @row-click="openDetails"
        >
          <template v-slot:body-cell-Datum="props">
            <q-td align="center">
              {{ formatDate(props.row.Datum) }}
            </q-td>
          </template>

          <template v-slot:body-cell-Baratel="props">
            <q-td align="left">
              {{ props.row.Baratel || "-" }}
            </q-td>
          </template>

          <template v-slot:body-cell-BrojBaranje="props">
            <q-td align="center">
              {{ getBrojBaranje(props.row) }}
            </q-td>
          </template>

          <template v-slot:body-cell-Opis="props">
            <q-td align="left">
              <div class="opis-preview">
                {{ shortText(props.row.Opis, 55) }}
              </div>
            </q-td>
          </template>

         <template v-slot:body-cell-StepenItnost="props">
  <q-td align="center">
    <q-badge :color="getStepenColor(props.row.StepenItnost)">
      {{ getStepenLabel(props.row.StepenItnost) }}
    </q-badge>
  </q-td>
</template>

          <template v-slot:body-cell-Status="props">
            <q-td align="center">
              <q-badge :color="getStatusColor(props.row.Status)">
                {{ props.row.Status || "-" }}
              </q-badge>
            </q-td>
          </template>
        </q-table>
      </q-card>

      <!-- Детален преглед под табелата -->
      <q-card
        v-if="hasSelectedRow"
        class="details-card q-pa-md shadow-xs rounded-2xl q-mt-md no-pdf"
      >
        <div class="details-header">
          <div class="details-title">
            <q-icon name="description" class="q-mr-sm" />
            Преглед на барање
          </div>

          <q-btn
            flat
            round
            dense
            icon="close"
            color="grey-7"
            @click="clearDetails"
          >
            <q-tooltip>Затвори</q-tooltip>
          </q-btn>
        </div>

        <q-separator class="q-my-md" />

        <div class="row q-col-gutter-md">
          <div class="col-12 col-md-6">
            <div class="detail-item">
              <div class="detail-label">Датум</div>
              <div class="detail-value">
                {{ formatDate(selectedRow.Datum) }}
              </div>
            </div>
          </div>

          <div class="col-12 col-md-6">
            <div class="detail-item">
              <div class="detail-label">Број на барање</div>
              <div class="detail-value">
                {{ getBrojBaranje(selectedRow) }}
              </div>
            </div>
          </div>

          <div class="col-12 col-md-6">
            <div class="detail-item">
              <div class="detail-label">Барател</div>
              <div class="detail-value">
                {{ selectedRow.Baratel || "-" }}
              </div>
            </div>
          </div>

          <div class="col-12 col-md-6">
            <div class="detail-item">
              <div class="detail-label">Апликација</div>
              <div class="detail-value">
                {{ selectedRow.Aplikacija || "-" }}
              </div>
            </div>
          </div>

          <div class="col-12 col-md-6">
            <div class="detail-item">
              <div class="detail-label">Итност</div>
              <div class="detail-value">
               <q-badge :color="getStepenColor(selectedRow.StepenItnost)">
  {{ getStepenLabel(selectedRow.StepenItnost) }}
</q-badge>
              </div>
            </div>
          </div>

          <div class="col-12 col-md-6">
            <div class="detail-item">
              <div class="detail-label">Статус</div>
              <div class="detail-value">
                <q-badge :color="getStatusColor(selectedRow.Status)">
                  {{ selectedRow.Status || "-" }}
                </q-badge>
              </div>
            </div>
          </div>

          <div class="col-12">
            <div class="detail-item">
              <div class="detail-label">Опис</div>
              <div class="detail-value long-text">
                {{ selectedRow.Opis || "-" }}
              </div>
            </div>
          </div>
        </div>
      </q-card>
    </div>

    <!-- Скриен документ за PDF -->
    <div class="pdf-hidden">
      <div ref="pdfContent" class="pdf-sheet">
        <div class="pdf-header">
          <div class="pdf-title">Преглед на поднесени барања</div>
          <div class="pdf-company">{{ companyName || "-" }}</div>
          <div class="pdf-date">Датум на печатење: {{ currentPrintDate }}</div>
        </div>

        <div class="pdf-filters-box">
          <div class="pdf-filters-title">Критериуми за преглед</div>

          <div class="pdf-meta-grid">
            <div>
              <strong>Број на барање:</strong>
              {{ filter.brojBaranje || "-" }}
            </div>

            <div>
              <strong>Статус:</strong>
              {{ selectedStatusLabel }}
            </div>

            <div>
              <strong>Од датум:</strong>
              {{ formatFilterDate(filter.from) }}
            </div>

            <div>
              <strong>До датум:</strong>
              {{ formatFilterDate(filter.to) }}
            </div>
          </div>
        </div>

        <table class="pdf-table">
          <thead>
            <tr>
              <th>Датум</th>
              <th>Барател</th>
              <th>Број на барање</th>
              <th>Апликација</th>
              <th>Опис</th>
              <th>Итност</th>
              <th>Статус</th>
            </tr>
          </thead>

          <tbody>
            <tr v-for="row in baranja" :key="row.ID">
              <td>{{ formatDate(row.Datum) }}</td>
              <td>{{ row.Baratel || "-" }}</td>
              <td>{{ getBrojBaranje(row) }}</td>
              <td>{{ row.Aplikacija || "-" }}</td>
              <td class="pdf-opis-cell">
                <div class="pdf-opis-text">
                  {{ row.Opis || "-" }}
                </div>
              </td>
              <td class="pdf-center">{{ getStepenLabel(row.StepenItnost) }}</td>
              <td>{{ row.Status || "-" }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </q-page>
</template>

<script>
import { Notify } from "quasar";
import { getUser } from "@/services/db";
import { getStatusi } from "@/services/statusiService";
import { pregledBaranja } from "@/services/baranjaApiService";

export default {
  name: "PreglediKlient",

  data() {
    return {
      baranja: [],
      statusOptions: [],
      selectedRow: {},
      companyName: "",
      loadingPdf: false,
      html2pdfReady: false,

      filter: {
        brojBaranje: "",
        from: "",
        to: "",
        status: null,
      },

      pagination: {
        rowsPerPage: 10,
      },

      columns: [
        {
          name: "Datum",
          label: "Датум",
          field: "Datum",
          align: "center",
          sortable: true,
        },
        {
          name: "Baratel",
          label: "Барател",
          field: "Baratel",
          align: "left",
          sortable: true,
        },
        {
          name: "BrojBaranje",
          label: "Број на барање",
          field: "BrojBaranje",
          align: "center",
          sortable: true,
        },
        {
          name: "Aplikacija",
          label: "Апликација",
          field: "Aplikacija",
          align: "left",
          sortable: true,
        },
        {
          name: "Opis",
          label: "Опис",
          field: "Opis",
          align: "left",
          sortable: false,
        },
        {
          name: "StepenItnost",
          label: "Итност",
          field: "StepenItnost",
          align: "center",
          sortable: true,
        },
        {
          name: "Status",
          label: "Статус",
          field: "Status",
          align: "center",
          sortable: true,
        },
      ],
    };
  },

  computed: {
    hasBrojBaranje() {
      return !!String(this.filter.brojBaranje || "").trim();
    },

    hasSelectedRow() {
      return !!this.selectedRow && Object.keys(this.selectedRow).length > 0;
    },

    selectedStatusLabel() {
      if (!this.filter.status) return "-";
      return this.filter.status.Opis || this.filter.status.label || "-";
    },

    currentPrintDate() {
      const d = new Date();
      const dd = String(d.getDate()).padStart(2, "0");
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const yyyy = d.getFullYear();
      const hh = String(d.getHours()).padStart(2, "0");
      const min = String(d.getMinutes()).padStart(2, "0");

      return `${dd}.${mm}.${yyyy} ${hh}:${min}`;
    },
  },

  methods: {
    todayISO() {
      const d = new Date();
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const dd = String(d.getDate()).padStart(2, "0");

      return `${yyyy}-${mm}-${dd}`;
    },

    onBrojBaranjeInput(val) {
      const onlyDigits = String(val || "").replace(/\D/g, "").slice(0, 10);

      if (onlyDigits !== this.filter.brojBaranje) {
        this.filter.brojBaranje = onlyDigits;
      }
    },

    normalizeBrojBaranje(value) {
      const onlyDigits = String(value || "").replace(/\D/g, "").trim();

      if (!onlyDigits) return "";

      const normalized = onlyDigits.replace(/^0+/, "");
      return normalized || "0";
    },

    normalizeRowBroj(row) {
      const broj = this.getBrojBaranje(row);
      return this.normalizeBrojBaranje(broj);
    },

    formatFilterDate(value) {
      if (!value) return "-";

      if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
        const [yyyy, mm, dd] = value.split("-");
        return `${dd}.${mm}.${yyyy}`;
      }

      return value;
    },

    goBack() {
      this.$router.push("/klient/moite-baranja");
    },

    async loadStatusi() {
      try {
        this.statusOptions = await getStatusi();
      } catch (err) {
        Notify.create({
          color: "negative",
          icon: "error",
          message: "Не може да се вчитаат статусите.",
        });
      }
    },

    async loadHtml2PdfScript() {
      if (window.html2pdf) {
        this.html2pdfReady = true;
        return;
      }

      await new Promise((resolve, reject) => {
        const existing = document.querySelector('script[data-html2pdf="true"]');

        if (existing) {
          existing.addEventListener("load", () => {
            this.html2pdfReady = true;
            resolve();
          });

          existing.addEventListener("error", reject);
          return;
        }

        const script = document.createElement("script");
        script.src =
          "https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.1/html2pdf.bundle.min.js";
        script.async = true;
        script.dataset.html2pdf = "true";

        script.onload = () => {
          this.html2pdfReady = true;
          resolve();
        };

        script.onerror = reject;
        document.head.appendChild(script);
      });
    },

    resetFilter() {
      this.filter = {
        brojBaranje: "",
        from: this.todayISO(),
        to: this.todayISO(),
        status: null,
      };

      this.baranja = [];
      this.selectedRow = {};
    },

    async exportPdf() {
      if (!this.baranja.length) {
        Notify.create({
          color: "warning",
          icon: "warning",
          message: "Нема податоци за печатење.",
        });
        return;
      }

      try {
        this.loadingPdf = true;

        await this.loadHtml2PdfScript();

        const element = this.$refs.pdfContent;

        if (!element || !window.html2pdf) {
          throw new Error("PDF библиотеката не е достапна.");
        }

        const options = {
          margin: [6, 6, 6, 6],
          filename: "pregled-na-podneseni-baranja.pdf",
          image: { type: "jpeg", quality: 1 },
          html2canvas: {
            scale: 2,
            useCORS: true,
            backgroundColor: "#ffffff",
            scrollX: 0,
            scrollY: 0,
          },
          jsPDF: {
            unit: "mm",
            format: "a4",
            orientation: "landscape",
          },
          pagebreak: {
            mode: ["css", "legacy"],
          },
        };

        const worker = window.html2pdf().set(options).from(element).toPdf();

        worker.get("pdf").then((pdf) => {
          const blobUrl = pdf.output("bloburl");
          window.open(blobUrl, "_blank");
        });
      } catch (err) {
        Notify.create({
          color: "negative",
          icon: "error",
          message: "Не може да се генерира документот.",
        });
      } finally {
        this.loadingPdf = false;
      }
    },

    openDetails(evt, row) {
      this.selectedRow = { ...row };
    },

    clearDetails() {
      this.selectedRow = {};
    },

    getBrojBaranje(row) {
      return row?.BrojBaranje || row?.Broj || row?.ID || "-";
    },

    shortText(text, max = 55) {
      if (!text) return "-";
      return text.length > max ? `${text.slice(0, max)}...` : text;
    },

    async applyFilter() {
      try {
        const user = getUser();
        const brojRaw = String(this.filter.brojBaranje || "").trim();
        const broj = this.normalizeBrojBaranje(brojRaw);

        if (!broj && (!this.filter.from || !this.filter.to)) {
          Notify.create({
            color: "warning",
            icon: "warning",
            message: "Внесете Од датум и До датум, или внесете број на барање.",
          });
          return;
        }

        const payload = {
          PartnerID: user?.id || 1,
          BrojBaranje: null,
          OdDatum: null,
          DoDatum: null,
          StatusID: null,
        };

        if (broj) {
          payload.OdDatum = "2000-01-01 00:00:00";
          payload.DoDatum = "2100-12-31 23:59:59";
          payload.StatusID = null;
        } else {
          payload.OdDatum = `${this.filter.from} 00:00:00`;
          payload.DoDatum = `${this.filter.to} 23:59:59`;
          payload.StatusID = this.filter.status ? this.filter.status.SifraID : null;
        }

        const result = await pregledBaranja(payload);
        let list = Array.isArray(result) ? result : [];

        if (broj) {
          list = list.filter((row) => this.normalizeRowBroj(row) === broj);
        }

        this.baranja = list;
        this.selectedRow = {};

        if (!this.baranja.length) {
          Notify.create({
            color: "warning",
            icon: "info",
            message: "Нема податоци за внесените услови.",
          });
        }
      } catch (err) {
        Notify.create({
          color: "negative",
          icon: "error",
          message: "Серверот не може да ги врати барањата.",
        });
      }
    },

    formatDate(isoString) {
      if (!isoString) return "-";

      const date = new Date(isoString);

      if (Number.isNaN(date.getTime())) return isoString;

      const dd = String(date.getDate()).padStart(2, "0");
      const mm = String(date.getMonth() + 1).padStart(2, "0");
      const yyyy = date.getFullYear();

      return `${dd}.${mm}.${yyyy}`;
    },

   getStepenLabel(stepen) {
  const value = Number(stepen);

  if (value === 1) return "1 - Најнизок";
  if (value === 2) return "2 - Низок";
  if (value === 3) return "3 - Среден";
  if (value === 4) return "4 - Висок";
  if (value === 5) return "5 - Најитен";

  return "-";
},

getStepenColor(stepen) {
  const value = Number(stepen);

  if (!value) return "grey";
  if (value === 1) return "green";
  if (value === 2) return "teal";
  if (value === 3) return "orange";
  if (value === 4) return "deep-orange";
  if (value === 5) return "red";

  return "grey";
},

    getStatusColor(status) {
      if (!status) return "grey";
      if (status.includes("Завршено")) return "green";
      if (status.includes("Откажано")) return "red";
      if (status.includes("На чекање")) return "orange";
      if (status.includes("уплата")) return "blue";

      return "grey";
    },
  },

  mounted() {
    this.loadStatusi();

    if (!this.filter.from) this.filter.from = this.todayISO();
    if (!this.filter.to) this.filter.to = this.todayISO();

    const user = getUser();

    this.companyName =
      user?.Naziv ||
      user?.naziv ||
      user?.Firma ||
      user?.firma ||
      user?.CompanyName ||
      user?.companyName ||
      "";

    this.loadHtml2PdfScript().catch(() => {});
  },
};
</script>

<style scoped>
.bg-light {
  background: #f7f8fa;
}

.text-mint {
  color: #0e6b5f !important;
}

.required-star {
  color: #d32f2f;
  font-weight: 700;
  margin-left: 2px;
}

.filter-card,
.table-card,
.details-card {
  background: #ffffff;
  border-radius: 14px;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.05);
}

.details-card {
  border: 1px solid #e5e7eb;
  background: #ffffff !important;
}

.btn-clean {
  background-color: #fff !important;
  border: 1.5px solid #0e6b5f !important;
  color: #0e6b5f !important;
  border-radius: 8px !important;
  transition: all 0.25s ease;
}

.btn-clean:hover {
  background-color: #f4fbfa !important;
  transform: translateY(-1px);
}

.filter-buttons-center {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  margin-top: 4px;
}

.action-btn {
  min-width: 150px;
  height: 40px;
  border-radius: 10px;
}

.q-table thead tr {
  background-color: #f1f6f5;
  color: #0e6b5f;
  font-weight: 600;
}

.q-table tbody tr:hover {
  background: #f8fbfa !important;
}

.clickable-table :deep(tbody tr) {
  cursor: pointer;
}

.opis-preview {
  max-width: 220px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.details-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.details-title {
  display: flex;
  align-items: center;
  font-size: 16px;
  font-weight: 700;
  color: #0e6b5f;
}

.detail-item {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 12px;
  min-height: 72px;
}

.detail-label {
  font-size: 12px;
  font-weight: 700;
  color: #6b7280;
  margin-bottom: 6px;
}

.detail-value {
  font-size: 14px;
  color: #1f2937;
  word-break: break-word;
}

.long-text {
  white-space: pre-wrap;
  line-height: 1.6;
}

.pdf-hidden {
  position: fixed;
  left: -99999px;
  top: 0;
  width: 1650px;
  z-index: -1;
  opacity: 0;
  pointer-events: none;
}

.pdf-sheet {
  background: #ffffff;
  color: #1f2937;
  padding: 14px 18px 18px;
  font-family: Arial, sans-serif;
}

.pdf-header {
  text-align: center;
  border-bottom: 2px solid #0e6b5f;
  padding-bottom: 8px;
  margin-bottom: 12px;
}

.pdf-title {
  font-size: 22px;
  font-weight: 700;
  color: #0e6b5f;
  margin-bottom: 4px;
}

.pdf-company {
  font-size: 17px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 4px;
}

.pdf-date {
  font-size: 11px;
  color: #6b7280;
}

.pdf-filters-box {
  border: 1px solid #d9e6e3;
  background: #f8fbfa;
  border-radius: 8px;
  padding: 10px 12px;
  margin-bottom: 12px;
}

.pdf-filters-title {
  font-size: 13px;
  font-weight: 700;
  color: #0e6b5f;
  margin-bottom: 8px;
}

.pdf-meta-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px 18px;
  font-size: 12px;
}

.pdf-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 11px;
  table-layout: fixed;
}

.pdf-table th,
.pdf-table td {
  border: 1px solid #cfd8d5;
  padding: 7px 8px;
  vertical-align: top;
  text-align: left;
  white-space: normal;
}

.pdf-table th {
  background: #0e6b5f;
  color: #ffffff;
  font-weight: 700;
}

.pdf-table tbody tr:nth-child(even) {
  background: #f8fbfa;
}

.pdf-table th:nth-child(1),
.pdf-table td:nth-child(1) {
  width: 10%;
}

.pdf-table th:nth-child(2),
.pdf-table td:nth-child(2) {
  width: 14%;
}

.pdf-table th:nth-child(3),
.pdf-table td:nth-child(3) {
  width: 11%;
}

.pdf-table th:nth-child(4),
.pdf-table td:nth-child(4) {
  width: 15%;
}

.pdf-table th:nth-child(5),
.pdf-table td:nth-child(5) {
  width: 24%;
}

.pdf-table th:nth-child(6),
.pdf-table td:nth-child(6) {
  width: 8%;
}

.pdf-table th:nth-child(7),
.pdf-table td:nth-child(7) {
  width: 18%;
}

.pdf-opis-cell {
  padding-top: 7px !important;
  padding-bottom: 7px !important;
}

.pdf-opis-text {
  display: block;
  max-width: 100%;
  white-space: pre-wrap !important;
  overflow-wrap: anywhere !important;
  word-break: break-all !important;
  hyphens: auto;
  line-height: 1.45;
}

.pdf-center {
  text-align: center !important;
}

@media (max-width: 768px) {
  .container-md {
    padding: 0 4px;
  }

  .filter-buttons-center {
    flex-direction: column;
    align-items: stretch;
  }

  .action-btn {
    width: 100%;
  }

  .opis-preview {
    max-width: 140px;
  }

  .details-header {
    align-items: center;
  }
}
</style>